import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BarcodeFormat } from '@zxing/browser';
import { ValueTypeEnum } from './enums/value-type.enum';
import { SupportedLanguagesType } from './localization/localization';
import { AlertStatus } from './models/alert-status';
import { AttributeDto, ValueTypeRestrictionDto } from './models/attribute-dto';
import { UserDto } from './models/user-dto';

export const allPropsNullish = (obj: unknown): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  const values = Object.values(obj as Record<string, unknown>);
  if (values.length === 0) return false;
  return values.every((v) => v == null); // == checks null or undefined
};

export const hasAlphaNum = (s: string) => /[\p{L}\p{N}]/u.test(s);
export const norm = (s?: string) => s?.normalize('NFKC') ?? '';

export const highlightTextMatch = (
  input: string,
  highlightValue: string | undefined,
  shouldHighlight: boolean,
  cssClass = 'highlight green',
): string => {
  if (!shouldHighlight || !highlightValue) return input;

  // Require at least one alphanumeric char in the match
  if (!/[A-Za-z0-9]/.test(highlightValue)) return input;

  const escaped = highlightValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escaped, 'gi');
  return input?.replace(regex, `<mark class="${cssClass}">$&</mark>`);
};

export function changeLanguage(lang: SupportedLanguagesType) {
  setCookie('SPARROW_USE_LANGUAGE', lang, 99999999);
  document.documentElement.lang = lang;
  location.reload();
}

export const isIntegerOnly = (str: string): boolean => {
  return /^-?\d+$/.test(str);
};

export const isNumericLikeString = (value: string): boolean => {
  if (typeof value !== 'string' || !value.trim()) return false;

  // Accept digits, dots, and commas only
  return /^[\d.,]+$/.test(value);
};

export const detectNumberFormatLocale = (value: string): 'de' | 'en' | null => {
  const hasDot = value.includes('.');
  const hasComma = value.includes(',');

  if (hasDot && hasComma) {
    const lastDot = value.lastIndexOf('.');
    const lastComma = value.lastIndexOf(',');
    return lastComma > lastDot ? 'de' : 'en';
  }

  if (hasComma) {
    const commaPos = value.lastIndexOf(',');
    const digitsAfter = value.length - commaPos - 1;
    const digitsBefore = value.slice(0, commaPos).replace(/\D/g, '').length;

    // If there are 3+ digits before and exactly 3 after → likely EN (thousands)
    if (digitsBefore >= 3 && digitsAfter === 3) return 'en';

    // If fewer than 3 digits after comma → likely decimal
    if (digitsAfter > 0 && digitsAfter <= 2) return 'de';
  }

  if (hasDot) {
    const dotPos = value.lastIndexOf('.');
    const digitsAfter = value.length - dotPos - 1;
    const digitsBefore = value.slice(0, dotPos).replace(/\D/g, '').length;

    if (digitsBefore >= 3 && digitsAfter === 3) return 'de'; // thousands for DE
    if (digitsAfter > 0 && digitsAfter <= 2) return 'en'; // decimal for EN
  }

  return null;
};

export const parseLocalizedNumericString = (value: string, locale: string): string => {
  if (locale === 'en') {
    return parseFloat(value.replace(/,/g, '')).toString();
  } else {
    return parseFloat(value.replace(/\./g, '').replace(/,/g, '.')).toString();
  }
};

export const formatAttributeValue = (
  newVal: string,
  valueTypeRestriction: ValueTypeRestrictionDto,
  customLocale?: string,
): string => {
  if (newVal === '' || newVal === undefined) return '';

  const lang = customLocale ?? getCookie('SPARROW_USE_LANGUAGE');
  if (valueTypeRestriction.valueType === ValueTypeEnum.Numeric && !valueTypeRestriction.integerOnly) {
    return parseLocalizedNumericString(newVal, lang);
  }

  return newVal;
};

export const formatLocalizedNumericAttributeValue = (currentVal: string | number | null): string | null => {
  if (currentVal === '' || currentVal === undefined || currentVal === null) return null;
  const lang = getCookie('SPARROW_USE_LANGUAGE');
  return new Intl.NumberFormat(lang, { minimumFractionDigits: 2 }).format(+currentVal);
};

/* Check if string is valid date */
export function checkIfValidDateFormat(str: string) {
  // yyyy-LL-dd
  const dateReg = /^\d{4}[./-]\d{2}[./-]\d{2}$/;

  return dateReg.test(str);
}

// split string by space, but ignore space in quotes
export const splitStringBySpace = (str: string): string[] => str.match(/(?:[^\s"]+|"[^"]*")+/g);

export function isNullOrUndefined(value: any): value is null | undefined {
  return value === null || value === undefined;
}

export function areAllPropsNullOrUndefined(obj: any): boolean {
  return Object.values(obj).every((value) => value === null || value === undefined);
}

export function isKeyOf<T extends object>(property: string | number | symbol, item: T): property is keyof T {
  return property in item;
}

export function removeUndefinedProperties<T>(obj: T): T {
  const result: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

export function deepCopy<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map((item) => deepCopy(item)) as unknown as T;
  } else if (typeof obj === 'object' && obj !== null) {
    const copy = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        copy[key] = deepCopy(obj[key]);
      }
    }
    return copy;
  }
  return obj;
}

export function addFragmentsToUrl(route: ActivatedRoute, router: Router, params: { [paramKey: string]: any }) {
  const urlTree = router.parseUrl(router.url);
  urlTree.queryParams = { ...urlTree.queryParams, ...params };
  router.navigateByUrl(urlTree);
}

export function removeFragmentFromUrl(route: ActivatedRoute, router: Router, paramKeys: string[]) {
  const urlTree = router.parseUrl(router.url);
  urlTree.queryParams = { ...urlTree.queryParams };
  paramKeys.forEach((paramKey) => delete urlTree.queryParams[paramKey]);
  router.navigateByUrl(urlTree);
}

export function validateAllFormFields(formGroup: FormGroup) {
  Object.keys(formGroup.controls).forEach((field) => {
    const control = formGroup.get(field);

    if (control instanceof FormControl) {
      control.markAsTouched();
      control.markAsPristine();
      control.updateValueAndValidity();
    } else if (control instanceof FormGroup) {
      validateAllFormFields(control);
    }
  });
}

export function getCookie(name: string): string {
  const nameLenPlus = name.length + 1;
  return (
    document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((cookie) => {
        return cookie.substring(0, nameLenPlus) === `${name}=`;
      })
      .map((cookie) => {
        return decodeURIComponent(cookie.substring(nameLenPlus));
      })[0] || null
  );
}

export function setCookie(name: string, value: any, days: number) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

function swapInArray(arr: any[], firstIndex: number, secondIndex: number) {
  const temp = arr[firstIndex];
  arr[firstIndex] = arr[secondIndex];
  arr[secondIndex] = temp;
}

function compareForAttrVal(a: { attribute: AttributeDto }, b: { attribute: AttributeDto }) {
  if (!a?.attribute.parentAttributeId) {
    return false;
  }
  const isParent = a?.attribute.parentAttributeId === b?.attribute.id;
  return !isParent;
}

export function compareAttrOrdering(a: number | null | undefined, b: number | null | undefined) {
  // ordering in a way we show first attr with values, and put null values to the bottom
  if (a === b) {
    return 0;
  } else if (a === null || a === undefined) {
    return 1;
  } else if (b === null || b === undefined) {
    return -1;
  } else {
    return a > b ? 1 : -1;
  }
}

// function for sorting AttributeWithValue array by their indent
// it sorts array by their indent values then it calls compareForAttrVal function
// to check if element has parentAttributeId, if it has and parentAttributeId is same as
// ID of some array element then it swaps its position so it could be child of its parent
export function sortAttrValArrayByIndent(array: { order?: number; indent?: number; attribute: AttributeDto }[]) {
  const sortByOrder = array.sort((a, b) => compareAttrOrdering(a.order, b.order));
  const sortByIndent = sortByOrder.sort((a, b) => a.indent - b.indent);
  const len = sortByIndent.length;

  for (let i = 0; i < len; i++) {
    for (let j = i; j > 0; j--) {
      if (compareForAttrVal(sortByIndent[j], sortByIndent[j - 1])) {
        swapInArray(sortByIndent, j, j - 1);
      } else {
        break;
      }
    }
  }

  return sortByIndent;
}

export function nFormatter(num: number, digits = 0, locale?: string) {
  const si = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i;
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break;
    }
  }
  return (
    (num / si[i].value)
      .toLocaleString(locale ?? 'en', { minimumFractionDigits: digits, maximumFractionDigits: digits })
      .replace(rx, '$1') + si[i].symbol
  );
}

export function arrayEquals(a: any[], b: any[]) {
  const x = a ? [...a] : null;
  const y = b ? [...b] : null;
  if (Array.isArray(x) && Array.isArray(y)) {
    x.sort();
    y.sort();
  }
  return (
    Array.isArray(x) &&
    Array.isArray(y) &&
    x.length === y.length &&
    x.every((val, index) => {
      if (typeof val === 'object' && typeof y[index] === 'object') {
        return checkObjEqual(val, y[index]);
      } else {
        return val === y[index];
      }
    })
  );
}

function isObject(object: any) {
  return object != null && typeof object === 'object';
}

export function checkObjPropsEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if both objects have the same properties
  const commonKeys = keys1.filter((key) => keys2.includes(key));

  // Check if the values of same-named properties are the same
  for (const key of commonKeys) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    const areObjects = typeof val1 === 'object' && typeof val2 === 'object';
    const areArrays = Array.isArray(val1) && Array.isArray(val2);

    if (
      (areObjects && !checkObjPropsEqual(val1, val2)) ||
      (areArrays && !arrayEquals(val1, val2)) ||
      (!areObjects && !areArrays && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

export function checkObjEqual(obj1: any, obj2: any) {
  if (isNullOrUndefined(obj1) || isNullOrUndefined(obj2)) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];
    if (Array.isArray(val1)) {
      if (!arrayEquals(val1, val2)) {
        return false;
      }
    } else {
      const areObjects = isObject(val1) && isObject(val2);
      if ((areObjects && !checkObjEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
        return false;
      }
    }
  }
  return true;
}

export function dataURLtoFile(dataurl: string, filename: string) {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function getStringHostname(url: string): string {
  const { hostname } = new URL(url);
  return hostname;
}

export function getFileTypeIcon(type: string): string {
  switch (type) {
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return 'xlsx-file';

    case 'application/pdf':
      return 'pdf-file';

    case 'application/msword':
      return 'doc-file';

    case 'text/csv':
      return 'csv-file';

    default:
      return 'general-file';
  }
}

export function isHardReload(): boolean {
  const alreadyVisited = sessionStorage.getItem('visitedOnce') === 'true';
  sessionStorage.setItem('visitedOnce', 'true');
  return alreadyVisited;
}

// Function to remove duplicates based on columnId and columnType
export function removeDuplicatesFromColumnData(list: any[]) {
  const uniqueColumns: any[] = [];
  const uniqueColumnSet = new Set();

  list.forEach((value) => {
    const { columnId, columnType } = value;
    const columnKey = `${columnId}-${columnType}`;

    if (!uniqueColumnSet.has(columnKey)) {
      uniqueColumnSet.add(columnKey);
      uniqueColumns.push(value);
    }
  });

  return uniqueColumns;
}

export const sortUsersByName = (data: UserDto[]): UserDto[] => {
  return data.sort((a, b) => {
    // First, compare by firstName
    if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) return -1;
    if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) return 1;

    // If firstName is the same, then compare by lastName
    if (a.lastName.toLowerCase() < b.lastName.toLowerCase()) return -1;
    if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) return 1;

    // If both are the same, return 0
    return 0;
  });
};

export const normalizeBooleanValue = (val: unknown): boolean => {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;
  if (typeof val === 'string') return val.toLowerCase() === 'true';
  return false;
};

export const getNumberMaskForLocale = (): any => {
  const lang = getCookie('SPARROW_USE_LANGUAGE');
  return lang?.toLowerCase() === 'de' ? numberMaskToComma : numberMaskToDot;
};

// EN loc
export const numberMaskToDot = {
  mask: Number, // enable number mask
  scale: 2, // digits after point, 0 for integers
  signed: false, // disallow negative
  thousandsSeparator: ',', // any single char
  padFractionalZeros: false, // if true, then pads zeros at end to the length of scale
  normalizeZeros: true, // appends or removes zeros at ends
  radix: '.', // fractional delimiter
  mapToRadix: ['.'], // symbols to process as radix
  min: 0,
};

// DE loc
export const numberMaskToComma = {
  mask: Number, // enable number mask
  scale: 2, // digits after point, 0 for integers
  signed: false, // disallow negative
  thousandsSeparator: '.', // any single char
  padFractionalZeros: false, // if true, then pads zeros at end to the length of scale
  normalizeZeros: true, // appends or removes zeros at ends
  radix: ',', // fractional delimiter
  mapToRadix: [','], // symbols to process as radix
  min: 0,
};

export const allowedNumericAndNavigationKeys = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
];
export const dialogMobileAdditionalData = {
  autoFocus: false,
  height: '100vh',
  width: '100vw',
  maxWidth: '100vw',
  panelClass: 'mobile-p0-grey-dialog',
};
// IETF BCP 47 language tag
export const localeIETFList = [
  'EN',
  'DE',
  'ES',
  'PT',
  'CS',
  'FR',
  'HU',
  'IT',
  'PL',
  'RO',
  'SK',
  'SL',
  'TR',
  'ZH',
  'JA',
  'KO',
  'TH',
  'ID',
  'SR',
  'HR',
];
export const megabyte = 1048576;
export const emailExistsRegex = /email.*already exists/i;
export const dynamicEnumListShowLimit = 15;
export const unixDayValueInMilliseconds = 24 * 60 * 60 * 1000;
export const defaultPageSizeOptions: number[] = [5, 20, 50, 100, 150];
export const defaultPageSize = defaultPageSizeOptions[1];
export const clientDefaultPageSizeOptions: number[] = [10, 25, 50];
export type SortDirection = 'ASC' | 'DESC' | '';
export const allowedScannerFormats = [
  BarcodeFormat.AZTEC,
  BarcodeFormat.CODABAR,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.MAXICODE,
  BarcodeFormat.PDF_417,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.RSS_14,
  BarcodeFormat.RSS_EXPANDED,
  BarcodeFormat.UPC_EAN_EXTENSION,
];

export const initialAlertState: AlertStatus = {
  isCalculating: false,
  isDismissed: false,
  showAlert: false,
};

export interface SortByDto {
  field: string;
  order: SortDirection;
}
